import { useMemo } from 'react'
import { LegalLinks } from '@/components/common/LegalLinks'
import { brandAssets } from '@/config/brandAssets'
import { LOGIN_COPY } from '@/features/auth/constants/loginCopy'
import { apexUrl } from '@/lib/tenantHost'

/** Inline text link used across the auth pages. */
export const authLinkClass =
  'font-semibold text-primary underline decoration-primary/30 underline-offset-4 transition-colors hover:decoration-primary focus:outline-none focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-primary/30'

interface AuthLayoutProps {
  /** Dark brand panel shown beside the form from `lg` up. */
  aside: React.ReactNode
  /** Right side of the top bar (e.g. "New here? Create an organization"). */
  headerAction?: React.ReactNode
  /** Panel width cap; the sign-up form needs more room than sign-in. */
  panelClassName?: string
  /** A tenant's own terms and privacy links, on its sign-in page. */
  legalLinks?: { termsUrl?: string; privacyUrl?: string }
  children: React.ReactNode
}

/**
 * Shared shell for the sign-in and sign-up pages: dark brand panel on the left,
 * form on a raised panel over a dotted surface on the right.
 */
export function AuthLayout({
  aside,
  headerAction,
  panelClassName = 'max-w-[27rem]',
  legalLinks,
  children,
}: AuthLayoutProps): React.ReactElement {
  return (
    <div className="flex min-h-dvh min-w-0 bg-[var(--public-bg)] pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] text-[var(--public-ink)] selection:bg-primary/15 lg:p-3">
      <main className="login-form-surface relative isolate flex min-h-dvh w-full min-w-0 flex-col px-5 py-6 sm:px-10 sm:py-8 lg:order-2 lg:min-h-0 lg:w-[46%] lg:px-12 xl:px-20">
        <header className="flex items-center justify-between gap-4">
          <a
            href={apexUrl('/')}
            className="flex items-center gap-2.5 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
          >
            <img src={brandAssets.logo} alt="" className="h-9 w-9 object-contain" />
            <span className="font-display text-lg font-semibold tracking-[-0.02em]">KAAMCO</span>
          </a>
          {headerAction && (
            <div className="hidden text-sm text-[var(--public-muted)] sm:block lg:hidden xl:block">
              {headerAction}
            </div>
          )}
        </header>

        <div className="flex flex-1 items-center py-10 sm:py-14">
          <div
            className={`mx-auto w-full min-w-0 rounded-[1.75rem] border border-[var(--public-border)]/70 bg-white p-6 shadow-[0_1px_2px_rgba(2,2,2,0.04),0_24px_60px_-24px_rgba(0,74,107,0.22)] sm:p-10 ${panelClassName}`}
          >
            {children}
          </div>
        </div>

        <footer className="flex flex-col gap-1 text-xs text-[var(--public-muted)] sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <span>{LOGIN_COPY.trustLine}</span>
          <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <LegalLinks termsUrl={legalLinks?.termsUrl} privacyUrl={legalLinks?.privacyUrl} />
            <span className="tabular-nums">© {new Date().getFullYear()} KAAMCO</span>
          </span>
        </footer>
      </main>

      <div className="hidden min-w-0 flex-1 lg:order-1 lg:flex">{aside}</div>
    </div>
  )
}

/** Archimedean spiral (r = a + bθ) — the brand motif, drawn from the centre outward. */
export function Spiral({ className }: { className?: string }): React.ReactElement {
  const pathD = useMemo(() => {
    const points: string[] = []
    const steps = 160
    const turns = 6
    for (let i = 0; i <= steps; i++) {
      const t = (i / steps) * turns * Math.PI * 2
      const r = 6 + 7 * t
      points.push(`${(300 + r * Math.cos(t)).toFixed(1)} ${(300 - r * Math.sin(t)).toFixed(1)}`)
    }
    return `M ${points.join(' L ')}`
  }, [])

  return (
    <svg viewBox="0 0 600 600" className={className} aria-hidden>
      <path d={pathD} fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
    </svg>
  )
}

interface AuthShowcaseProps {
  headline: string
  headlineMuted: string
  subhead: string
  /** The product vignette centred in the panel. */
  children: React.ReactNode
  /** Quiet list along the bottom edge. */
  footer: React.ReactNode
}

/** Dark brand panel: two-tone headline, a vignette over the spiral, and a footer list. */
export function AuthShowcase({
  headline,
  headlineMuted,
  subhead,
  children,
  footer,
}: AuthShowcaseProps): React.ReactElement {
  return (
    <section
      className="relative isolate flex w-full flex-col overflow-hidden rounded-[2rem] bg-[var(--public-dark)] px-10 py-12 text-white xl:px-14 xl:py-14"
      aria-labelledby="auth-showcase-heading"
    >
      <div className="relative">
        <h2
          id="auth-showcase-heading"
          className="max-w-[18ch] font-display text-[clamp(2.25rem,3.2vw,3.25rem)] font-semibold leading-[1.04] tracking-[-0.04em] text-balance"
        >
          {headline} <span className="text-white/40">{headlineMuted}</span>
        </h2>
        <p className="mt-4 max-w-[44ch] text-base leading-relaxed text-white/65">{subhead}</p>
      </div>

      <div className="relative flex flex-1 items-center justify-center py-12">
        <Spiral className="pointer-events-none absolute left-1/2 top-1/2 h-[44rem] w-[44rem] -translate-x-1/2 -translate-y-1/2 text-white/[0.09]" />
        {children}
      </div>

      <div className="relative border-t border-white/10 pt-6">{footer}</div>
    </section>
  )
}
